import React from "react";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";

import {
	Card,
	CardContent,
	CardHeader,
	CardTitle
} from "src/common/components/Card";
import { MiniForm, MiniFormList, MiniFormWizard } from "src/features/forms";
import { usePatientIdParam } from "src/features/patient";
import { LeafNode, Node, ParentNode } from "src/modules/shared/form-explorer";

interface FormRendererProps {
	forms: Node[];
}

export const FormRenderer: React.FunctionComponent<FormRendererProps> = ({
	forms
}) => {
	const patientId = usePatientIdParam();
	const { formId = "" } = useParams<"formId">();
	const map = React.useMemo(() => createNodeMap(forms), [forms]);
	const node = React.useMemo(() => map[formId], [map, formId]);
	return (
		<Card key={formId}>
			<CardHeader>
				<CardTitle>
					<FormattedMessage {...node.value.title} />
				</CardTitle>
			</CardHeader>
			<CardContent>
				{"children" in node ? (
					<ParentRenderer patientId={patientId} node={node} />
				) : (
					<LeafRenderer patientId={patientId} node={node} />
				)}
			</CardContent>
		</Card>
	);
};

interface ParentRendererProps {
	patientId: string;
	node: ParentNode;
}

const ParentRenderer: React.FunctionComponent<ParentRendererProps> = ({
	patientId,
	node
}) => {
	const forms = React.useMemo(
		() => findAllLeaves(node).map(node => ({ id: node.id, ...node.value })),
		[node]
	);
	if (node.value.type === "list") {
		return (
			<MiniFormList
				patientId={patientId}
				forms={forms}
				columns={node.value.columns}
			/>
		);
	} else {
		return <MiniFormWizard patientId={patientId} forms={forms} />;
	}
};

interface LeafRendererProps {
	patientId: string;
	node: LeafNode;
}

const LeafRenderer: React.FunctionComponent<LeafRendererProps> = ({
	patientId,
	node
}) =>
	node.value.isDummy ? null : (
		<MiniForm patientId={patientId} form={{ id: node.id, ...node.value }} />
	);

function createNodeMap(nodes: Node[]): { [id: string]: Node } {
	const queue = [...nodes];
	const map: { [id: string]: Node } = {};
	while (queue.length !== 0) {
		const node = queue.shift();
		if (node === undefined) {
			throw new Error();
		}
		map[node.id] = node;
		if ("children" in node) {
			for (const child of node.children) {
				queue.push(child);
			}
		}
	}
	return map;
}

function findAllLeaves(node: ParentNode): LeafNode[] {
	const queue: ParentNode[] = [node];
	const leaves: LeafNode[] = [];
	while (queue.length !== 0) {
		const node = queue.shift();
		if (node === undefined) {
			throw new Error();
		}
		for (const child of node.children) {
			if ("children" in child) {
				queue.push(child);
			} else {
				leaves.push(child);
			}
		}
	}
	return leaves;
}
